import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Favicon from "react-favicon";
import { Toaster } from "react-hot-toast";

import "semantic-ui-css/semantic.min.css";
import "katex/dist/katex.min.css";
import "highlight.js/styles/github.css";

import "./style/bootstrap.css";
import "./style/custom.css";

// Layout
import NavigationBar from "./components/layout/NavigationBar";
import Footer from "./components/layout/Footer";

// Pages
import RootPage from "./pages/RootPage";
import HomePage from "./pages/HomePage";
import SearchResultsPage from "./pages/SearchResultsPage";
import ProfilePage from "./pages/ProfilePage";
import MetricsPage from "./pages/MetricsPage";

// Auth
import SignUp from "./components/auth/SignUp";
import SignIn from "./components/auth/SignIn";
import Activate from "./components/auth/Activate";
import PostSignUp from "./components/auth/PostSignUp";
import ResetPassword from "./components/auth/ResetPassword";
import ResetPasswordConfirm from "./components/auth/ResetPasswordConfirm";

// Projects
import ProjectDetail from "./components/project/ProjectDetail";
import ProjectCreate from "./components/project/ProjectCreate";
import ProjectUpdate from "./components/project/ProjectUpdate";

// Settings (with nested routing inside)
import Settings from "./components/settings/Settings";

// Common
import ErrorBoundary from "./components/common/ErrorBoundary";

// Auth actions
import { VALIDATE_TOKEN } from "./components/auth/AuthActions";

// Marketing (new)
import AboutPage from "./components/marketing/AboutPage";
import TermsPage from "./components/marketing/TermsPage";
import PrivacyPage from "./components/marketing/PrivacyPage";
import ContactPage from "./components/marketing/ContactPage";

const App = () => {
  useEffect(() => {
    VALIDATE_TOKEN();
  }, []);

  return (
    <HelmetProvider>
    <ErrorBoundary>
    <Router>
      <div>
        <Favicon url="https://i.imgur.com/QABosqY.png" />
        <NavigationBar />

        {/* Global toast portal */}
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: "#000000e6",
              color: "#59c6c1",
              borderRadius: "8px",
              padding: "12px 16px",
              fontSize: "0.9rem",
            },
            success: {
              iconTheme: { primary: "#59c6c1", secondary: "#000000e6" },
            },
            error: {
              style: { background: "#fff1f2", color: "#991b1b" },
            },
          }}
        />

        <Routes>
          {/* Auth */}
          <Route path="/accounts/register" element={<SignUp />} />
          <Route path="/accounts/signin" element={<SignIn />} />
          <Route path="/accounts/activate" element={<Activate />} />
          <Route path="/accounts/confirm" element={<PostSignUp />} />
          <Route path="/accounts/password-reset" element={<ResetPassword />} />
          <Route
            path="/accounts/password-reset/confirm"
            element={<ResetPasswordConfirm />}
          />

          {/* App Pages */}
          <Route path="/" element={<RootPage />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/search" element={<SearchResultsPage />} />

          {/* Settings */}
          <Route path="/settings/*" element={<Settings />} />

          {/* Projects */}
          <Route path="/projects/new" element={<ProjectCreate />} />
          <Route path="/projects/:id/edit" element={<ProjectUpdate />} />
          <Route path="/projects/:id" element={<ProjectDetail />} />

          {/* Admin */}
          <Route path="/system" element={<MetricsPage />} />
          <Route path="/metrics" element={<Navigate to="/system" replace />} />

          {/* Marketing (keep these BEFORE the username catch-all) */}
          <Route path="/about" element={<AboutPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/contact" element={<ContactPage />} />

          {/* User Profile */}
          <Route path="/:username" element={<ProfilePage />} />

          {/* 404 (optional) */}
          {/* <Route path="*" element={<div>404 Not Found</div>} /> */}
        </Routes>

        <Footer />
      </div>
    </Router>
    </ErrorBoundary>
    </HelmetProvider>
  );
};

export default App;
